import { Flex, Select } from "antd";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { getAllCustomerByBusinessId } from "hooks/queries/customer";
import { getAllStaffByBusinessId } from "hooks/queries/staff";
import { ScheduleFilters } from "pages/schedule/filter/ScheduleFilterContext";
import { ResetFilterButton } from "pages/schedule/component/ResetFilterButton";
import React, {
  SetStateAction,
  Dispatch,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { translate } from "shared/languages";
import { UserType } from "shared/types/enum";

export const ScheduleListFilter: React.FC<{
  filters: ScheduleFilters;
  setFilters: Dispatch<SetStateAction<ScheduleFilters>>;
  // Rendered first in the row (the list's Hari Ini button).
  leading?: ReactNode;
}> = ({ filters, setFilters, leading }) => {
  const { instructorIds, customerIds, classIds } = filters;

  // Fetch filter options
  const { data: customers, isLoading: customerLoading } =
    getAllCustomerByBusinessId({ is_active: true });
  const { data: staffs, isLoading: staffLoading } = getAllStaffByBusinessId(
    UserType.Instructor,
    { is_active: true }
  );
  // All classes (Open + Private) — the list shows both types merged.
  const { data: classes, isLoading: classLoading } =
    getAllClassTypeByBusinessId(undefined);

  // Memoize sorted options to prevent unnecessary re-sorting
  const customerOptions = useMemo(
    () =>
      customers
        ?.slice()
        .sort((a, b) => a.full_name.localeCompare(b.full_name))
        .map((c) => ({
          label: c.full_name,
          value: c.id,
        })) || [],
    [customers]
  );

  const staffOptions = useMemo(
    () =>
      staffs
        ?.sort((a, b) => a.full_name.localeCompare(b.full_name))
        .map((s) => ({
          label: s.full_name,
          value: s.id,
        })) || [],
    [staffs]
  );

  const classOptions = useMemo(
    () =>
      classes
        ?.sort((a, b) => a.name.localeCompare(b.name))
        .map((cls) => ({
          label: cls.name,
          value: cls.id,
        })) || [],
    [classes]
  );

  // Filters come back with the list on Back. Drop any pick that is no longer
  // an option (a client or instructor deactivated, a class deleted
  // meanwhile), or its Select would show the raw id.
  useEffect(() => {
    if (!customers || !staffs || !classes) return;
    const keep = (ids: string[], options: { id: string }[]) =>
      ids.filter((id) => options.some((o) => o.id === id));
    const next = {
      customerIds: keep(customerIds, customers),
      instructorIds: keep(instructorIds, staffs),
      classIds: keep(classIds, classes),
    };
    if (
      next.customerIds.length !== customerIds.length ||
      next.instructorIds.length !== instructorIds.length ||
      next.classIds.length !== classIds.length
    ) {
      setFilters((prev) => ({ ...prev, ...next }));
    }
  }, [
    customers,
    staffs,
    classes,
    customerIds,
    instructorIds,
    classIds,
    setFilters,
  ]);

  // Filter change handlers
  const handleInstructorChange = (values: string[]) => {
    setFilters((prev) => ({ ...prev, instructorIds: values }));
  };

  const handleCustomerChange = (values: string[]) => {
    setFilters((prev) => ({ ...prev, customerIds: values }));
  };

  const handleClassChange = (values: string[]) => {
    setFilters((prev) => ({ ...prev, classIds: values }));
  };

  const filtersActive =
    instructorIds.length > 0 || customerIds.length > 0 || classIds.length > 0;

  const onReset = () =>
    setFilters((prev) => ({
      ...prev,
      instructorIds: [],
      customerIds: [],
      classIds: [],
    }));

  return (
    <Flex gap={16} align="center" style={{ paddingBottom: 16 }}>
      {leading}
      <Select
        mode="multiple"
        allowClear
        placeholder={translate("instructor")}
        loading={staffLoading}
        options={staffOptions}
        value={instructorIds}
        onChange={handleInstructorChange}
        maxTagCount="responsive"
        showSearch
        optionFilterProp="label"
        style={{ minWidth: 200, flex: 1 }}
      />
      <Select
        mode="multiple"
        allowClear
        placeholder={translate("customer")}
        loading={customerLoading}
        options={customerOptions}
        value={customerIds}
        onChange={handleCustomerChange}
        maxTagCount="responsive"
        showSearch
        optionFilterProp="label"
        style={{ minWidth: 200, flex: 1 }}
      />
      <Select
        mode="multiple"
        allowClear
        placeholder={translate("label.class_name")}
        loading={classLoading}
        options={classOptions}
        value={classIds}
        onChange={handleClassChange}
        maxTagCount="responsive"
        showSearch
        optionFilterProp="label"
        style={{ minWidth: 200, flex: 1 }}
      />
      <ResetFilterButton active={filtersActive} onReset={onReset} />
    </Flex>
  );
};
